import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';

import { api } from '@/lib/api';
import { cn } from '@/lib/utils';
import { Popover, PopoverTrigger, PopoverContent } from '@/components/ui/popover';
import { timeAgo } from '@/lib/display-format';

/**
 * HOW CURRENT IS WHAT I AM LOOKING AT - answered on every screen.
 *
 * Every report here reads computed daily facts now, and the first question a
 * computed number raises is whether today is in it. Left unanswered, a reader
 * either distrusts a number that is current or trusts one that is stale, and
 * the second one costs money. So the answer sits beside the date picker, in one
 * word, with the three clocks behind it one click away.
 *
 * Deliberately NOT a green dot that is always green: the chip earns attention
 * by changing. It goes amber when ad spend falls behind, because that is the
 * feed that genuinely lags and the one that silently drags every ROAS on the
 * platform down while it does.
 */

const STATE = {
  live: { dot: 'bg-emerald-500', label: 'Live', tone: 'text-muted-foreground' },
  'catching-up': { dot: 'bg-sky-500 animate-pulse', label: 'Updating', tone: 'text-muted-foreground' },
  building: { dot: 'bg-sky-500 animate-pulse', label: 'Building history', tone: 'text-muted-foreground' },
  error: { dot: 'bg-rose-500', label: 'Data behind', tone: 'text-rose-600 dark:text-rose-400' },
};

/** "just now" / "6m ago" / "3h ago" / "2d ago" - the shortest true form. */
// Minutes since, in the product's one wording (lib/display-format timeAgo).
function ago(min) { return min == null ? null : timeAgo(Date.now() - min * 60 * 1000); }

function FeedRow({ f }) {
  // A feed that has never delivered says so. A zero would read as a verdict on
  // the store rather than as a connection that was never made.
  const late = f.mode === 'sync' && f.days_behind != null && f.days_behind >= 2;
  const never = !f.last_at;
  return (
    <div className="flex items-baseline justify-between gap-4 border-b border-border/60 py-1.5 last:border-0">
      <span className="flex items-center gap-2">
        <span className={cn('size-1.5 shrink-0 rounded-full', f.lost ? 'bg-rose-500' : never ? 'bg-muted-foreground/40' : late ? 'bg-amber-500' : 'bg-emerald-500')} />
        <span className="text-[13.5px] font-medium">{f.label}</span>
      </span>
      <span className="text-right text-[13px] text-muted-foreground">
        {never ? 'nothing yet' : f.detail}
        {f.last_at && <span className="ml-1.5 tabular-nums opacity-70">{ago(f.ago_min)}</span>}
      </span>
    </div>
  );
}

/**
 * The pulse, read once and shared: the status dot in the date picker and the
 * Alerts bell both read it (same query key, one fetch). `worst` is the worst
 * true thing, not the average one.
 */
export function usePulse() {
  const { data } = useQuery({
    queryKey: ['metrics', 'pulse'],
    queryFn: async () => (await api.get('/api/metrics/pulse')).data,
    // Ambient, not a live tile: cheap enough to keep honest, quiet enough not
    // to matter. A minute is under the facts' own rebuild latency.
    staleTime: 60_000,
    refetchInterval: 120_000,
    retry: false,
  });
  if (!data) return null;
  const feeds = data.feeds || [];
  const lateSpend = feeds.filter((f) => f.mode === 'sync' && f.days_behind != null && f.days_behind >= 2);
  const st = STATE[data.state] || STATE.live;
  // Tracking lost outranks everything: counted "as it happens" is not true of
  // site activity while the tag is off the live site.
  const lost = data.tracking?.lost ? data.tracking : null;
  const spendLag = data.state === 'live' && lateSpend.length ? Math.max(...lateSpend.map((f) => f.days_behind)) : 0;
  const label = lost ? 'Tracking lost' : spendLag ? `Spend ${spendLag}d behind` : st.label;
  const dot = lost ? 'bg-rose-500' : spendLag ? 'bg-amber-500' : st.dot;
  const tone = lost ? 'text-rose-600 dark:text-rose-400' : spendLag ? 'text-amber-700 dark:text-amber-400' : st.tone;
  return { data, feeds, lateSpend, lost, spendLag, label, dot, tone };
}

// What the dot says on hover, in a sentence: the dot alone is a colour.
function pulseSentence(p) {
  if (p.lost) return 'Tracking lost: site activity is incomplete until the tracker is back on your live site.';
  if (p.spendLag) return `Ad spend is ${p.spendLag} days behind. Orders and site activity are live, today included.`;
  if (p.data.state === 'live') return 'Live: orders and site activity are counted as they happen, today included.';
  if (p.data.state === 'error') return 'Data behind: the reports could not rebuild.';
  return `${p.label}: the reports are catching up with the latest orders.`;
}

/**
 * The freshness dot, inside the date picker: "is today in this?" asked in the
 * same breath as "which days". A dot, not a pill, and it still earns attention
 * by changing: green live, blue updating, amber when ad spend lags, rose when
 * tracking is lost. Hover says it in a sentence; a click opens the three clocks.
 */
export function PulseDot({ className }) {
  const p = usePulse();
  if (!p) return null;
  return <PulsePopover p={p} trigger={(
    <button type="button" title={pulseSentence(p)} aria-label={`Data freshness: ${p.label}. ${pulseSentence(p)}`}
      className={cn('grid size-6 place-items-center rounded-full outline-none transition-colors hover:bg-muted focus-visible:ring-2 focus-visible:ring-ring', className)}>
      <span className={cn('size-2 rounded-full', p.dot)} />
    </button>
  )} />;
}

function PulsePopover({ p, trigger }) {
  const { data, feeds, lateSpend, lost } = p;
  return (
    <Popover>
      <PopoverTrigger asChild>{trigger}</PopoverTrigger>
      <PopoverContent align="end" className="w-[340px] p-4">
        <p className="text-[14px] font-semibold">How current this is</p>
        {lost && (
          <div className="mt-2 rounded-md border card-critical px-3 py-2 text-[13px] text-red-800 dark:text-red-200">
            <p className="font-semibold">{lost.title}</p>
            <p className="mt-0.5">
              Site activity in these numbers is incomplete until the tracker is back on your live site
              {lost.kept_by_pixel ? ' (the Shopify pixel is keeping page views meanwhile)' : ''}.{' '}
              <Link to={lost.action?.href || '/settings?tab=tracking&sub=install'} className="font-medium underline underline-offset-2">
                {lost.action?.label || 'Get the site script'}
              </Link>
            </p>
          </div>
        )}
        <p className="mt-0.5 text-[13px] leading-relaxed text-muted-foreground">
          Orders and site activity are counted as they happen, so today is always included. Ad spend arrives on
          each platform&apos;s own schedule.
        </p>

        <div className="mt-3">
          {feeds.map((f) => <FeedRow key={f.key} f={f} />)}
        </div>

        <div className="mt-3 border-t border-border pt-2 text-[13px] text-muted-foreground">
          {data.facts?.error ? (
            <p className="text-rose-600 dark:text-rose-400">Reports could not rebuild: {data.facts.error}</p>
          ) : data.facts?.computed_at ? (
            <p>
              Reports rebuilt <span className="font-medium text-foreground">{ago(data.facts.computed_ago_min)}</span>
              {data.facts.dirty && ', rebuilding again now'}
              {data.today_so_far?.orders > 0 && <>, including <span className="font-medium text-foreground">{data.today_so_far.orders.toLocaleString('en-IN')}</span> order{data.today_so_far.orders === 1 ? '' : 's'} today</>}.
            </p>
          ) : (
            <p>Building this workspace&apos;s history now. Reports fill in as it finishes.</p>
          )}
          {data.facts?.history_from && (
            <p className="mt-0.5">
              History from {new Date(`${data.facts.history_from}T00:00:00`).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}.
            </p>
          )}
          {lateSpend.length > 0 && (
            <p className="mt-1.5">
              A platform behind on spend drags every return number it touches.{' '}
              <Link to="/integrations?cat=spend" className="font-medium text-primary underline-offset-2 hover:underline">Check the connection</Link>.
            </p>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}

export default PulseDot;
